/**
 * Lock screen shell for User PIN and User Switch modes.
 * Shared layout: dark background, logo, clinic name + location, content area, sign-in link.
 * PIN/setup screens use the Figma "hd logo" header (node 10453:38867): wordmark + Scout on dark.
 */

import type { ReactNode } from "react";
import hdLogoDark from "@/assets/hd-logo-dark.png";
import { HALLOWEEN_LOGO_ON_DARK_CLASS, HALLOWEEN_LOGO_SRC, isHalloween } from "@/common/utils/halloween";

export type LockScreenMode = "pin" | "switch";

export interface LockScreenProps {
    /** Clinic display name */
    clinicName: string;
    /** Clinic location (e.g. "Main Street") */
    clinicLocation?: string | null;
    /** PIN mode: numpad + PIN boxes. Switch mode: avatar grid. */
    mode: LockScreenMode;
    /** Content area: PinEntry or UserSwitchGrid */
    children: ReactNode;
    /** Header: "default" = wordmark only (inverted); "hd-dark" = Figma hd logo (wordmark + Scout) for PIN lock/setup */
    logo?: "default" | "hd-dark";
    /** Optional class for the root */
    className?: string;
    /** Signs out and goes to email login. Omit for preview-only screens (link is inert). */
    onSignInWithEmailInstead?: () => void | Promise<void>;
}

export default function LockScreen({
    clinicName,
    clinicLocation,
    children,
    logo = "default",
    className = "",
    onSignInWithEmailInstead,
}: LockScreenProps) {
    const useHdDarkLogo = logo === "hd-dark";

    return (
        <div
            className={`fixed inset-0 z-[100] flex flex-col ${useHdDarkLogo ? "bg-[#131821]" : "bg-gradient-to-b from-shadow-950 to-shadow-700"} ${className}`}
            role="dialog"
            aria-label="Device lock screen"
        >
            {/* Logo: Figma 10453:38867 (hd logo) for PIN/setup; else wordmark only. EN-1476: the Halloween logo overrides both while isHalloween(). */}
            <div className={`flex justify-center pt-10 pb-4 ${useHdDarkLogo ? "items-center" : ""}`}>
                {isHalloween() ? (
                    // EN-1476: outline + glow keep the art's dark hat and bat visible on the dark background.
                    <img
                        src={HALLOWEEN_LOGO_SRC}
                        alt="HappyDoc"
                        className={`h-14 w-auto object-contain ${HALLOWEEN_LOGO_ON_DARK_CLASS}`}
                    />
                ) : useHdDarkLogo ? (
                    <img src={hdLogoDark} alt="HappyDoc" className="h-[45px] w-auto object-contain" />
                ) : (
                    <img
                        src="/HD-Horizontal-Blue.png"
                        alt="HappyDoc"
                        className="h-8 w-auto brightness-0 invert opacity-95"
                    />
                )}
            </div>

            {/* Clinic name + location */}
            <div className="text-center px-4 pb-6">
                <h2 className={`text-lg font-semibold ${useHdDarkLogo ? "text-white" : "text-shadow-100"}`}>
                    {clinicName || "Clinic"}
                </h2>
                {clinicLocation && (
                    <p className={`text-sm mt-1 ${useHdDarkLogo ? "text-white/80" : "text-shadow-300"}`}>
                        {clinicLocation}
                    </p>
                )}
            </div>

            {/* Content: PinEntry or UserSwitchGrid */}
            <div className="flex-1 flex flex-col items-center justify-start px-4">{children}</div>

            {/* Sign in with email instead */}
            <div className="px-4 pb-6 text-center">
                <button
                    type="button"
                    className={`text-sm font-medium underline focus:outline-none focus:ring-2 rounded bg-transparent border-0 p-0 cursor-pointer font-inherit ${useHdDarkLogo ? "text-white/90 hover:text-white focus:ring-white/40" : "text-shadow-200 hover:text-shadow-100 focus:ring-max-400"}`}
                    onClick={() => {
                        void onSignInWithEmailInstead?.();
                    }}
                >
                    Sign in with email instead
                </button>
            </div>

            {/* Keyboard shortcut hint */}
            <p className={`text-xs text-center pb-4 ${useHdDarkLogo ? "text-white/50" : "text-shadow-500"}`}>
                Lock screen: ⌘⇧L (Mac) / Ctrl+Shift+L (Windows)
            </p>
        </div>
    );
}
